import { BlockVariant, ButtonBlockProps, DynamicBlock, DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { MEETING_STATUS } from '@/types/meeting.types';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface IGetNewMeetingBlocksProps {
  loading: BehaviorSubject<boolean>;
  save: () => void;
}

interface IGetEditMeetingBlocksProps {
  store: MeetingRepository;
  status: MEETING_STATUS;
  saveAsDraft?: () => void;
  loadingDraft: BehaviorSubject<boolean>;
  validateDraft?: () => void;
  loadingValidate: BehaviorSubject<boolean>;
  cancel?: () => void;
  loadingCancel: BehaviorSubject<boolean>;
  deleteMeeting?: () => void;
  loadingDelete: BehaviorSubject<boolean>;
  meetingDate?: string;
  syncParticipants?: () => void;
  loadingSyncParticipants?: BehaviorSubject<boolean>;
  launchMeeting?: () => void;
}

interface IGetConveneMeetingBlocksProps {
  status: MEETING_STATUS;
  save?: () => void;
  isSavingLoading?: BehaviorSubject<boolean>;
  isPVNull$?: BehaviorSubject<boolean>;
  downloadPvDraft?: () => void;
  downloadPv?: () => void;
  validate: () => void;
  openUploadDialog: () => void;
  inspectPV?: () => void;
  checkPvExtraits?: () => void | undefined;
  isPVSigned: boolean;
  cancel?: () => void;
  loadingCancel: BehaviorSubject<boolean>;
  canBeCancelled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MeetingWorkflowBlocksService {
  getNewMeetingBlocks({ loading, save }: IGetNewMeetingBlocksProps): DynamicBlocks {
    return {
      '0': [
        {
          variant: BlockVariant.BUTTON,
          props: {
            label: 'meeting.actions.save',
            onClick: () => save(),
            loading,
          },
        },
      ],
    };
  }

  getEditMeetingBlocks({
    store,
    status,
    cancel,
    saveAsDraft,
    validateDraft,
    deleteMeeting,
    loadingDraft,
    loadingValidate,
    loadingCancel,
    loadingDelete,
    meetingDate,
    syncParticipants,
    loadingSyncParticipants,
    launchMeeting,
  }: IGetEditMeetingBlocksProps): DynamicBlocks {
    const saveAsDraftButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.save_as_draft',
        onClick: () => saveAsDraft?.(),
        loading: loadingDraft,
      },
    };

    const validateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.save',
        onClick: () => validateDraft?.(),
        loading: loadingValidate,
        disabled: store.getObservable('isEqual'),
      },
    };

    const launchButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.launch',
        onClick: () => launchMeeting?.(),
      },
    };

    // const delayButton: DynamicBlock<ButtonBlockProps> = {
    //   variant: BlockVariant.BUTTON,
    //   props: {
    //     label: 'meeting.actions.delay',
    //     onClick: () => console.log('Delay clicked'),
    //   },
    // };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.cancel',
        onClick: () => cancel?.(),
        loading: loadingCancel,
      },
    };

    const deleteButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.delete',
        onClick: () => deleteMeeting?.(),
        loading: loadingDelete,
      },
    };

    const syncParticipantsButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        severity: 'info',
        label: 'meeting.actions.sync_participants',
        onClick: () => syncParticipants?.(),
        loading: loadingSyncParticipants,
      },
    };

    if (status === MEETING_STATUS.BROUILLON) {
      return {
        '0': [saveAsDraftButton, validateButton, deleteButton],
        '1': [syncParticipantsButton],
      };
    }

    const launch = meetingDate && new Date(meetingDate) < new Date();

    if (status === MEETING_STATUS.PLANIFIER) {
      if (launch) {
        return {
          '0': [validateButton, launchButton, cancelButton],
        };
      }
      return {
        '0': [validateButton, cancelButton],
      };
    }

    if (status === MEETING_STATUS.NON_PLANIFIER) {
      return {
        '0': [validateButton, cancelButton],
      };
    }

    if (status === MEETING_STATUS.EN_COURS) {
      return {
        '0': [saveAsDraftButton, validateButton],
      };
    }

    return {
      '0': [saveAsDraftButton],
    };
  }

  getConveneMeetingBlocks({
    status,
    save,
    isSavingLoading,
    isPVNull$,
    downloadPvDraft,
    downloadPv,
    validate,
    openUploadDialog,
    inspectPV,
    checkPvExtraits,
    isPVSigned,
    cancel,
    loadingCancel,
    canBeCancelled,
  }: IGetConveneMeetingBlocksProps): DynamicBlocks {
    const downloadDraftPVButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.downloadDraftPV',
        disabled: isPVNull$,
        onClick: () => downloadPvDraft?.(),
      },
    };

    const validateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.validate',
        onClick: () => validate?.(),
      },
    };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.cancel',
        onClick: () => cancel?.(),
        loading: loadingCancel,
      },
    };

    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.save',
        onClick: () => save?.(),
        loading: isSavingLoading,
      },
    };

    const downloadPVButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.downloadPV',
        onClick: () => downloadPv?.(),
      },
    };

    const signedPVButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.signedPV',
        onClick: () => openUploadDialog(),
      },
    };

    const inspectPVButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.inspectPV',
        onClick: () => inspectPV?.(),
      },
    };

    const checkPvExtraitsButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.viewPvSnippets',
        onClick: () => checkPvExtraits?.(),
      },
    };

    if (status === MEETING_STATUS.VALIDER) {
      return {
        '0': !isPVSigned ? [downloadPVButton, signedPVButton] : [inspectPVButton],
        '1': checkPvExtraits ? [checkPvExtraitsButton] : [],
      };
    }
    if (status === MEETING_STATUS.EN_COURS) {
      return {
        '0': canBeCancelled ? [downloadDraftPVButton, saveButton, validateButton, cancelButton] : [downloadDraftPVButton, saveButton, validateButton],
      };
    }

    if (status === MEETING_STATUS.CLOTURER) {
      return {
        '0': [inspectPVButton],
        '1': checkPvExtraits ? [checkPvExtraitsButton] : [],
      };
    }

    return {
      '0': [],
    };
  }

  getInspectConvenedBlocks({ inspectPV, checkPvExtraits }: { inspectPV: () => void; checkPvExtraits?: () => void }): DynamicBlocks {
    const inspectPVButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.inspectPV',
        onClick: () => inspectPV?.(),
      },
    };

    const checkPvExtraitsButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'meeting.actions.viewPvSnippets',
        onClick: () => checkPvExtraits?.(),
      },
    };

    return {
      '0': [inspectPVButton],
      '1': checkPvExtraits ? [checkPvExtraitsButton] : [],
    };
  }
}
